import { get, handleError } from '@/lib/http-adapter';
import { AvailableDay } from '@/state/available-classes';
import { ClassAvailabilities } from '@/state/class-availabilities';
import { Class } from '@/state/class-groups';

export type EffectivePrice =
  | { type: 'cash'; amount: number; currency: string }
  | {
      type: 'credit';
      cost: number;
      remaining: number;
      limit_one_seat_per_class: boolean;
      source: 'pack' | 'subscription';
    }
  | {
      type: 'unlimited';
      limit_one_seat_per_class: boolean;
      source: 'pack' | 'subscription';
    };

// With a class time, the backend also counts a credit the customer holds on
// that time for a waitlist place, which she can spend on it.
export type PricedOccurrence = {
  slot_id: string | null;
  override_slot_id?: string | null;
  start_time: string;
};

export const getEffectivePrice = async ({
  class_id,
  occurrence,
}: {
  class_id: string;
  occurrence?: PricedOccurrence | null;
}) => {
  try {
    return await get<{ effective_price: EffectivePrice }>(
      `/public/classes/${class_id}/effective-price`,
      {
        params: occurrence && {
          slot_id: occurrence.slot_id,
          override_slot_id: occurrence.override_slot_id,
          start_time: occurrence.start_time,
        },
      }
    );
  } catch (error) {
    handleError(error);
  }
};

export const getClassDetails = async ({ slug }: { slug: string }) => {
  try {
    const response = await get<{ class: Class }>(`/public/classes/${slug}`);
    return response?.class ?? null;
  } catch (error) {
    handleError(error);
  }
};

export const getClassAvailabilities = async (params?: {
  slug: string;
  start_date: string;
  end_date: string;
  timezone: string;
}) => {
  try {
    const response = await get<ClassAvailabilities>(
      `/public/classes/${params?.slug}/availability`,
      {
        params: {
          start_date: params?.start_date,
          end_date: params?.end_date,
          timezone: params?.timezone,
        },
      }
    );

    return response?.days ?? [];
  } catch (error) {
    handleError(error);
  }
};

export const getAvailableClasses = async (queryParams?: {
  start_date: string;
  end_date: string;
  timezone: string;
  response_mode?: 'full' | 'summary';
}) => {
  try {
    const response = await get<{
      days: AvailableDay[];
      next_week_days?: AvailableDay[];
    }>(`/public/classes/availability`, {
      params: {
        ...queryParams,
      },
    });

    return response;
  } catch (error) {
    handleError(error);
  }
};
